import { COMPACT_TOOLTIP_STYLE } from '@src/components/common/tooltipStyles';
import RedlinesIcon from '@src/svgs/RedlinesIcon';
import ReplaceTermIcon from '@src/svgs/ReplaceTermIcon';
import React from 'react';
import { Tooltip } from 'react-tooltip';
import styled, { css } from 'styled-components';

import { PanelButton } from './panelButtonStyles';
import { REDLINE_REMOVED_COLOR } from './redlineColors';

interface RedlineToggleButtonProps {
  /**
   * PLAT-10795: this toggle wears two hats. On the redline surfaces (Creator
   * Studio's LeftSidePanel and the clip preview's ClipTranscriptBridge, which
   * pass `clipTimelineMarkers`) it keeps the original track-changes identity;
   * on the Content Studio T&S tab it's the replaced-terms toggle — previously
   * that surface fell into a generic "See full transcript" copy that said
   * nothing about replaced terms.
   */
  isRedlineSurface: boolean;
  isOn: boolean;
  /**
   * A Show Only filter owns the header (PLAT-11182): the alternate view has no
   * notion of the filtered paragraphs, so the toggle goes inert alongside the
   * Quotes/Clips pills — and the host turns the view off on the mode flip, so
   * it is never stuck on behind a disabled control.
   */
  blocked?: boolean;
  onToggle: () => void;
  onFocus?: () => void;
}

/** `removed` word color in the redlines view (`_TranscriptionDiff`). */

/**
 * The transcript-header redline / replaced-terms view toggle. Extracted from
 * `Transcript.tsx` per CLAUDE.md → "Architecture Guardrails" rule 5 when it
 * gained the Show Only inert state (PLAT-11182).
 */
export const RedlineToggleButton: React.FC<RedlineToggleButtonProps> = ({
  isRedlineSurface,
  isOn,
  blocked,
  onToggle,
  onFocus,
}) => {
  const subject = isRedlineSurface ? 'transcript redlines' : 'replaced terms';
  return (
    <>
      <ViewToggleButton
        tabIndex={-1}
        onFocus={onFocus}
        isPressed={isOn && !blocked}
        $disabled={blocked}
        aria-disabled={!!blocked}
        aria-label={
          isRedlineSurface ? 'Show transcript redlines' : 'Show replaced terms'
        }
        data-testid="transcript-redline-button"
        data-tooltip-id="history-tooltip"
        data-tooltip-content={
          blocked
            ? `Turn off "Show Only" to see ${subject}`
            : `${isOn ? 'Hide' : isRedlineSurface ? 'See' : 'Show'} ${subject}`
        }
        onClick={blocked ? undefined : onToggle}
      >
        {/* T&S tab: A→B glyph, shared with the context menu's "Replace Term"
            action — a pencil there read as a duplicate of the adjacent Review
            Translations pencil. */}
        {isRedlineSurface ? (
          /* The struck page (PLAT-11877). Off state previews what the view
             shows: the struck line wears the same red as a removed word in
             the redlines view. Pressed (gold) and inert (muted) states stay
             monochrome. */
          <RedlinesIcon
            width="16"
            height="16"
            strokeColor="currentColor"
            strikeColor={
              !isOn && !blocked ? REDLINE_REMOVED_COLOR : 'currentColor'
            }
          />
        ) : (
          <ReplaceTermIcon width="17" height="14" strokeColor="currentColor" />
        )}
      </ViewToggleButton>
      <Tooltip
        id="history-tooltip"
        style={COMPACT_TOOLTIP_STYLE}
        place="bottom"
        positionStrategy="fixed"
      />
    </>
  );
};

/* `aria-disabled` rather than native `disabled` so the tooltip explaining WHY
   the control is inert still fires — the same idiom as the toggle pills. */
const ViewToggleButton = styled(PanelButton)<{ $disabled?: boolean }>`
  ${props =>
    props.$disabled &&
    css`
      color: var(--text-muted);
      cursor: default;

      &:hover {
        background-color: transparent;
        color: var(--text-muted);
      }

      &:active {
        background-color: transparent;
        scale: 1;
      }
    `}
`;
